// Zentrale deutsche Übersetzungen für Status-Werte in der Admin-/Master-Ansicht.
// Wird von Master-Panel, Kundenverwaltung und Order-Views verwendet, damit
// nirgendwo mehr englische Rohtexte („invoiced", „completed", „checked_in")
// auftauchen. Datum-Formatierung siehe `formatDeDate`.

export const APPOINTMENT_STATUS_LABELS = {
  scheduled: 'Geplant',
  confirmed: 'Bestätigt',
  checked_in: 'Eingecheckt',
  in_progress: 'In Arbeit',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  no_show: 'Nicht erschienen',
};

export const ORDER_STATUS_LABELS = {
  pending: 'Ausstehend',
  confirmed: 'Bestätigt',
  processing: 'In Bearbeitung',
  shipped: 'Versandt',
  delivered: 'Ausgeliefert',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  invoiced: 'Berechnet',
  paid: 'Bezahlt',
};

export const QUOTE_STATUS_LABELS = {
  draft: 'Entwurf',
  sent: 'Versendet',
  accepted: 'Angenommen',
  rejected: 'Abgelehnt',
  revision_requested: 'Überarbeitung',
  converted: 'Umgewandelt',
  invoiced: 'Rechnung',
  paid: 'Bezahlt',
  payment_pending: 'Zahlung offen',
  payment_received: 'Zahlung erhalten',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  archived: 'Archiviert',
  overdue: 'Überfällig',
  open: 'Offen',
};

export const APPROVAL_STATUS_LABELS = {
  pending: 'In Prüfung',
  approved: 'Genehmigt',
  rejected: 'Abgelehnt',
  open: 'Offen',
  closed: 'Geschlossen',
};

/** Fallback-freundlicher Übersetzer: kennt der Map den Key nicht → capitalize */
export const humanStatus = (raw, map) => {
  if (!raw) return '';
  const key = String(raw).toLowerCase();
  if (map && map[key]) return map[key];
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
};

// Live-Status-Fortschritt (Werkstatt-Auftrag: Fahrzeugannahme -> fertig).
// Der Prozent-Regler ist die primäre Steuergröße; die Stufen dienen nur noch
// als grobe, für Kunde/Werkstatt lesbare Orientierung entlang der Skala.
export const LIVE_STATUS_STEPS = [
  { value: 'angenommen', label: 'Angenommen', color: 'bg-blue-400', textColor: 'text-blue-400', percent: 0 },
  { value: 'in_bearbeitung', label: 'In Bearbeitung', color: 'bg-yellow-400', textColor: 'text-yellow-400', percent: 17 },
  { value: 'waschen', label: 'Waschen', color: 'bg-cyan-400', textColor: 'text-cyan-400', percent: 33 },
  { value: 'politur', label: 'Politur/Versiegelung', color: 'bg-purple-400', textColor: 'text-purple-400', percent: 50 },
  { value: 'innenraum', label: 'Innenreinigung', color: 'bg-orange-400', textColor: 'text-orange-400', percent: 67 },
  { value: 'qualitaetskontrolle', label: 'Qualitätskontrolle', color: 'bg-pink-400', textColor: 'text-pink-400', percent: 83 },
  { value: 'fertig', label: 'Fertig', color: 'bg-green-400', textColor: 'text-green-400', percent: 100 },
];

/** Stufe (Label + Farbe) für einen gegebenen Prozentwert — nächstgelegener Anker. */
export const liveStatusStepForPercent = (percent) => {
  const p = Number(percent) || 0;
  return LIVE_STATUS_STEPS.reduce((closest, step) => (
    Math.abs(step.percent - p) < Math.abs(closest.percent - p) ? step : closest
  ), LIVE_STATUS_STEPS[0]);
};

/** Prozentwert für einen Auftrag — nutzt gespeicherten Prozentwert, sonst
 * Rückfall auf den Anker der (alten) Status-Stufe, sonst 0. */
export const liveStatusPercentOf = (appt) => {
  if (appt?.live_status_percent != null && appt.live_status_percent !== '') return Number(appt.live_status_percent);
  if (appt?.live_status) return LIVE_STATUS_STEPS.find(s => s.value === appt.live_status)?.percent ?? 0;
  return 0;
};

/**
 * Datum → 25.12.2026 (immer deutsch TT.MM.JJJJ, nie US-Format).
 * Falls `withTime` und eine Uhrzeit vorhanden ist → "25.12.2026 14:30".
 * Akzeptiert Date-Objekte, ISO-Strings ("2026-12-25", "2026-12-25T14:30:00Z")
 * und bereits deutsche Strings ("25.12.2026").
 */
export const formatDeDate = (val, withTime = false) => {
  if (!val) return '';

  // Reiner Datums-String "JJJJ-MM-TT" (evtl. mit Uhrzeit-Teil) — ohne Date()
  // parsen, damit keine Zeitzonen-Verschiebung den Tag ändert.
  if (typeof val === 'string') {
    // Reines Datum ohne Zeitzone → direkt umformen (keine TZ-Verschiebung).
    const dOnly = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dOnly) return `${dOnly[3]}.${dOnly[2]}.${dOnly[1]}`;
    // Datum + lokale Zeit ohne Zeitzonen-Angabe (kein Z / +hh:mm am Ende).
    const dLocal = val.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?![:.\d]*(?:Z|[+-]\d{2}))/);
    if (dLocal) {
      const base = `${dLocal[3]}.${dLocal[2]}.${dLocal[1]}`;
      return withTime ? `${base} ${dLocal[4]}:${dLocal[5]}` : base;
    }
    // Bereits deutsches Format "TT.MM.JJJJ"
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return val;
  }

  const d = val instanceof Date ? val : new Date(val);
  if (isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  if (!withTime) return `${dd}.${mm}.${yyyy}`;
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
};

/** Datum + Uhrzeit → "25.12.2026 14:30" (Kurzform für formatDeDate(v, true)). */
export const formatDeDateTime = (val) => formatDeDate(val, true);

/**
 * Zeitstempel aus dem Backend ohne Zonenangabe (BSON-datetime, z. B. created_at)
 * sind UTC. formatDeDate liest sie als Ortszeit und zeigt dadurch 2 Stunden zu
 * früh an. Hier wird UTC angenommen und in deutscher Zeit ausgegeben.
 */
export const formatUtcDateTime = (val) => {
  if (!val) return '';
  let s = val;
  if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s) && !/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    s = `${s.replace(' ', 'T')}Z`;
  }
  const d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return formatDeDate(val, true);
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d).replace(', ', ' ');
};

export const CHECKIN_STATUS_LABELS = {
  created: 'Erstellt',
  active: 'Aktiv',
  checked_in: 'Eingecheckt',
  in_progress: 'In Arbeit',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

// Termin-Zeitpunkt inkl. optionalem Zeitraum (end_date/end_time), z.B.
// "21.09.2026 11:00", "21.09.2026 11:00–18:00" oder
// "21.09.2026 11:00 – 22.09.2026 18:00".
export const formatAppointmentWhen = (apt) => {
  if (!apt) return '';
  const fullDay = apt.duration_mode === 'full_day';
  const start = formatDeDate(apt.scheduled_date);
  const sTime = !fullDay && apt.scheduled_time ? ` ${apt.scheduled_time}` : '';
  const multi = apt.end_date && String(apt.end_date).slice(0, 10) !== String(apt.scheduled_date).slice(0, 10);
  if (!multi) {
    const t = apt.scheduled_time ? ` ${apt.scheduled_time}` : '';
    return `${start}${t}${apt.end_time && apt.scheduled_time ? `–${apt.end_time}` : ''}`;
  }
  return `${start}${sTime} – ${formatDeDate(apt.end_date)}${apt.end_time ? ` ${apt.end_time}` : ''}`;
};
